import React, { useEffect, useRef, useState, useCallback } from 'react';

interface OAuthButtonProps {
  label?: string;
  authUrl: string;            // Full authorization URL built by backend
  disabled?: boolean;
  onSuccess: (code: string, state: string) => void;
  onError: (error: string) => void;
  onAbandoned?: () => void;
}

type OAuthStatus = 'idle' | 'connecting' | 'connected' | 'error';

export const OAuthButton: React.FC<OAuthButtonProps> = ({
  label = 'Sign in',
  authUrl,
  disabled = false,
  onSuccess,
  onError,
  onAbandoned,
}) => {
  const [status, setStatus] = useState<OAuthStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const popupRef = useRef<Window | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const stateRef = useRef<string>('');
  // Ref tracks connecting state so the poll interval doesn't read stale React state
  const connectingRef = useRef(false);
  // Stable ref to the message handler so removeEventListener always targets the same function
  const handleMessageRef = useRef<((e: MessageEvent) => void) | null>(null);
  // Guard state updates after unmount
  const mountedRef = useRef(true);
  // Prevents onAbandoned firing when the popup closes after a successful postMessage delivery
  const messageReceivedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      cleanup();
    };
  }, []);

  const generateState = (): string => {
    return Array.from(
      window.crypto.getRandomValues(new Uint8Array(20)),
      b => b.toString(16).padStart(2, '0')
    ).join('');
  };

  const cleanup = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (handleMessageRef.current) {
      window.removeEventListener('message', handleMessageRef.current);
      handleMessageRef.current = null;
    }
    connectingRef.current = false;
  }, []);

  const openPopup = () => {
    if (disabled || status === 'connecting') return;

    const state = generateState();
    stateRef.current = state;

    const separator = authUrl.includes('?') ? '&' : '?';
    const fullUrl = `${authUrl}${separator}state=${state}`;

    const w = 600, h = 700;
    const left = Math.round(window.screenX + (window.outerWidth - w) / 2);
    const top = Math.round(window.screenY + (window.outerHeight - h) / 2);
    const features = `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no,popup=yes`;

    setStatus('connecting');
    setErrorMessage(null);
    connectingRef.current = true;
    messageReceivedRef.current = false;

    // Register the message handler BEFORE opening the popup to eliminate the race window
    // where the popup posts its OAUTH_CALLBACK message before the parent has attached the listener.
    const handler = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (!event.data || event.data.type !== 'OAUTH_CALLBACK') return;

      messageReceivedRef.current = true;
      cleanup();
      if (!mountedRef.current) return;

      if (event.data.error) {
        setStatus('error');
        setErrorMessage(event.data.error);
        onError(event.data.error);
        return;
      }

      // Validate state (CSRF protection)
      if (event.data.state !== stateRef.current) {
        setStatus('error');
        setErrorMessage('Security validation failed. Please try again.');
        onError('State mismatch — possible CSRF attempt');
        return;
      }

      stateRef.current = ''; // single-use: clear after validation

      if (event.data.code) {
        setStatus('connected');
        onSuccess(event.data.code, event.data.state);
      }
    };

    handleMessageRef.current = handler;
    window.addEventListener('message', handler);

    popupRef.current = window.open(fullUrl, 'oauth_popup', features);

    if (!popupRef.current) {
      connectingRef.current = false;
      cleanup(); // removes the listener registered above
      setStatus('error');
      setErrorMessage('Popup was blocked. Please allow popups for this site.');
      onError('Popup blocked');
      return;
    }

    // Poll for popup close — only fires onAbandoned if no message was received first.
    // Guards the race where the popup closes (300 ms after postMessage) before the
    // parent window has processed the message event.
    pollRef.current = setInterval(() => {
      if (popupRef.current?.closed) {
        const wasConnecting = connectingRef.current;
        const hadMessage = messageReceivedRef.current;
        cleanup();
        if (wasConnecting && !hadMessage && mountedRef.current) {
          setStatus('idle');
          onAbandoned?.();
        }
      }
    }, 250);
  };

  if (status === 'connected') {
    return (
      <div className="oauth-connected">
        <span className="oauth-connected-icon">✓</span>
        <span>Connected</span>
      </div>
    );
  }

  return (
    <div className="oauth-button-wrapper">
      <button
        type="button"
        className={`btn oauth-btn ${status === 'connecting' ? 'oauth-btn--connecting' : 'btn-secondary'}`}
        onClick={openPopup}
        disabled={disabled || status === 'connecting'}
      >
        {status === 'connecting' ? (
          <>
            <span className="oauth-spinner" />
            Connecting...
          </>
        ) : (
          label
        )}
      </button>
      {errorMessage && (
        <p className="oauth-error">{errorMessage}</p>
      )}
    </div>
  );
};

export default OAuthButton;
